/**
 * Authentication API client
 */

import { getAPIClient } from './client';

export interface LoginRequest {
  username: string;
  password: string;
  remember_me?: boolean;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    username: string;
    full_name?: string;
    tenant_id?: string;
    is_email_verified: boolean;
  };
  requires_password_reset?: boolean; // True if user must reset password (e.g., default password)
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
  full_name?: string;
  tenant_id?: string;
}

export interface RegisterResponse {
  user_id: string;
  email: string;
  username: string;
  message: string;
  email_verification_required: boolean;
  email_verification_token?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirmRequest {
  token: string;
  new_password: string;
}

export interface ForcedPasswordResetRequest {
  new_password: string;
}

/**
 * Login with username and password
 */
export async function login(request: LoginRequest): Promise<LoginResponse> {
  const client = getAPIClient();
  
  // Debug logging in development
  if (import.meta.env.DEV) {
    const baseURL = (client as any).baseURL;
    console.log('[Auth API] Login request:', {
      baseURL,
      endpoint: '/auth/login',
      fullURL: `${baseURL}/auth/login`,
      username: request.username,
    });
  }
  
  return client.post<LoginResponse>('/auth/login', request);
}

/**
 * Register new user
 */
export async function register(request: RegisterRequest): Promise<RegisterResponse> {
  const client = getAPIClient();
  return client.post<RegisterResponse>('/auth/register', request);
}

/**
 * Logout current user
 */
export async function logout(): Promise<void> {
  const client = getAPIClient();
  await client.post('/auth/logout', {});
}

/**
 * Refresh access token
 */
export async function refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
  const client = getAPIClient();
  return client.post<RefreshTokenResponse>('/auth/refresh', request);
}

/**
 * Request password reset
 */
export async function requestPasswordReset(request: PasswordResetRequest): Promise<{ message: string }> {
  const client = getAPIClient();
  return client.post('/auth/password-reset/request', request);
}

/**
 * Confirm password reset
 */
export async function confirmPasswordReset(request: PasswordResetConfirmRequest): Promise<{ message: string }> {
  const client = getAPIClient();
  return client.post('/auth/password-reset/confirm', request);
}

/**
 * Forced password reset (for default password, uses JWT token)
 */
export async function forcedPasswordReset(request: ForcedPasswordResetRequest): Promise<{ message: string }> {
  const client = getAPIClient();
  return client.post('/auth/password-reset/forced', request);
}

/**
 * Verify email
 */
export async function verifyEmail(token: string): Promise<{ message: string; email_verified: boolean }> {
  const client = getAPIClient();
  return client.post('/auth/verify-email', { token });
}











